import Dexie, { type EntityTable } from 'dexie';
import type { ContactEvent, PoseSequence, SwingMetrics } from '@tbc/core';
import type { CourtTimelineSample } from './court/types';

// -----------------------------------------------------------------------------
// Peer-agent types — imported from @tbc/core when they land. Until then, we
// declare local fallbacks so this package typechecks independently.
// TODO: replace with @tbc/core exports once the coaching / match-state /
// shot-classifier agents publish them.
// -----------------------------------------------------------------------------

/** One of the players in the session (solo = just 'p1'). */
export interface Player {
  readonly id: 'p1' | 'p2';
  readonly displayName: string;
  readonly dominantSide: 'left' | 'right';
}

/** Scoreboard snapshot at session end. */
export interface MatchState {
  readonly setsWonP1: number;
  readonly setsWonP2: number;
  readonly gamesWonP1: number;
  readonly gamesWonP2: number;
  readonly currentPointP1: 0 | 15 | 30 | 40 | 'AD';
  readonly currentPointP2: 0 | 15 | 30 | 40 | 'AD';
  readonly isTiebreak: boolean;
}

/** Rough shot classification. `'unknown'` means classifier was not run. */
export type ShotType =
  | 'forehand'
  | 'backhand'
  | 'serve'
  | 'volley'
  | 'smash'
  | 'unknown';

/** A rule from @tbc/core/coaching that fired on a particular swing. */
export interface RuleHit {
  readonly ruleId: string;
  readonly severity: 'info' | 'warn' | 'critical';
  readonly messagePtBr: string;
}

// -----------------------------------------------------------------------------
// v1 schema (kept for read-only migration compatibility)
// -----------------------------------------------------------------------------

/**
 * Legacy v1 session: one continuous video blob, raw pose sequence, and
 * per-swing metrics. Written until schema bump. New sessions land in v2.
 */
export interface SessionRecord {
  id: string;
  schemaVersion?: 1;
  createdAtMs: number;
  durationMs: number;
  fps: number;
  videoBlob: Blob;
  poseSequence: PoseSequence;
  contacts: ContactEvent[];
  swings: SwingMetrics[];
  /**
   * Per-frame player + ball samples captured during live analysis,
   * persisted so SwingDetail can replay court positions synchronized to
   * the stored `videoBlob`. Optional for backwards compatibility with v1
   * sessions recorded before the replay feature landed.
   */
  courtTimeline?: readonly CourtTimelineSample[];
}

export interface SwingThumbnailRecord {
  id: string; // `${sessionId}#${contactIndex}`
  sessionId: string;
  contactIndex: number;
  thumbnailBlob: Blob;
}

// -----------------------------------------------------------------------------
// v2 schema — "best moments" compact sessions
// -----------------------------------------------------------------------------

/**
 * A single retained swing: enough pose + a thumbnail to re-render the moment,
 * without the originating video blob. Pose window is gzipped-JSON to keep the
 * IndexedDB footprint tiny.
 */
export interface CompactSwing {
  readonly swingId: string; // `${sessionId}#${i}`
  readonly playerId: 'p1' | 'p2';
  readonly shotType: ShotType;
  readonly metrics: SwingMetrics;
  /**
   * Compressed pose window around the contact. Call `decodePoseSeries` to get
   * back a `PoseFrame[]`. Compression is gzip + base64 when
   * `CompressionStream` is available, else plain JSON string.
   */
  readonly poseSeriesJson: string;
  readonly thumbnailBlob: Blob;
  readonly coachingHits: readonly RuleHit[];
}

/**
 * A compact session: no raw video blob, only the top-N swings (default 20)
 * selected by peak wrist angular velocity, plus match state + coaching hits.
 */
export interface CompactSessionRecord {
  id: string;
  schemaVersion: 2;
  createdAtMs: number;
  durationMs: number;
  fps: number;
  players: readonly Player[];
  matchState: MatchState | null;
  swings: readonly CompactSwing[];
  // videoBlob: intentionally absent.
}

/** Union returned by `getSession` / `listSessions` — caller narrows on `schemaVersion`. */
export type AnySessionRecord = SessionRecord | CompactSessionRecord;

// -----------------------------------------------------------------------------
// Dexie database
// -----------------------------------------------------------------------------

/**
 * v3 additive: a single-row user-settings table. We keep all prior stores to
 * preserve v1 / v2 sessions across the upgrade.
 *
 * The row itself is typed in `lib/settings/settings.svelte.ts` — declaring
 * the Dexie table here with a loose record type avoids a circular import
 * between `db.ts` and the reactive-settings module.
 */
export interface SettingsRow {
  id: 'user';
  [k: string]: unknown;
}

class TbcDatabase extends Dexie {
  sessions!: EntityTable<SessionRecord, 'id'>;
  thumbnails!: EntityTable<SwingThumbnailRecord, 'id'>;
  compactSessions!: EntityTable<CompactSessionRecord, 'id'>;
  settings!: EntityTable<SettingsRow, 'id'>;

  constructor(name = 'tbc-v1') {
    super(name);

    // v1 schema: raw-video sessions + per-swing thumbnails.
    this.version(1).stores({
      sessions: 'id, createdAtMs',
      thumbnails: 'id, sessionId, contactIndex',
    });

    // v2 schema: add compact ("best moments") sessions table. The v1 stores
    // stay readable so that old recordings survive the upgrade. New writes go
    // into `compactSessions` via `saveBestMomentsSession`.
    this.version(2).stores({
      sessions: 'id, createdAtMs',
      thumbnails: 'id, sessionId, contactIndex',
      compactSessions: 'id, createdAtMs',
    });

    // v3 schema: add `settings` (one-row user preferences). Existing sessions
    // and compact sessions are untouched. The upgrade function seeds a row
    // with defaults so first-run consumers always find something.
    this.version(3)
      .stores({
        sessions: 'id, createdAtMs',
        thumbnails: 'id, sessionId, contactIndex',
        compactSessions: 'id, createdAtMs',
        settings: 'id',
      })
      .upgrade(async (tx) => {
        // Seed defaults inline (avoids importing the settings module, which
        // itself imports `db`). Must stay in sync with `DEFAULT_SETTINGS`.
        const defaults: SettingsRow = {
          id: 'user',
          contactThresholdSpeed: 0.8,
          contactKSigma: 2.0,
          contactRefractoryMs: 600,
          overlays: {
            stickman: true,
            miniCourt: true,
            ball: true,
            courtPolygon: true,
            ballTrail: true,
          },
          detectionFps: 15,
          recalibrateEverySec: 4,
          handedness: 'right',
          sceneChangeThreshold: 30,
          pauseOnCut: true,
        };
        await tx.table('settings').put(defaults);
      });
  }
}

export const db = new TbcDatabase();

export function newSessionId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// -----------------------------------------------------------------------------
// Read helpers — transparently cover v1 + v2
// -----------------------------------------------------------------------------

function normalizeV1(r: SessionRecord): SessionRecord {
  // Force the discriminant so callers can switch on schemaVersion uniformly.
  return r.schemaVersion === 1 ? r : { ...r, schemaVersion: 1 };
}

/** Fetch one session, searching v2 first, then falling back to v1. */
export async function getSession(id: string): Promise<AnySessionRecord | undefined> {
  const v2 = await db.compactSessions.get(id);
  if (v2) return v2;
  const v1 = await db.sessions.get(id);
  return v1 ? normalizeV1(v1) : undefined;
}

/**
 * List every session (v1 + v2) sorted by `createdAtMs` desc. Returns the
 * union type; callers pick fields based on `schemaVersion`.
 */
export async function listSessions(): Promise<AnySessionRecord[]> {
  const [v1Rows, v2Rows] = await Promise.all([
    db.sessions.toArray(),
    db.compactSessions.toArray(),
  ]);
  const all: AnySessionRecord[] = [
    ...v1Rows.map(normalizeV1),
    ...v2Rows,
  ];
  all.sort((a, b) => b.createdAtMs - a.createdAtMs);
  return all;
}
